/** ---------------------------------------------------------------------------------------
 * --  LICENSE AGREEMENT -- *
 This software is licensed under the following terms:

 * -- GRANT OF LICENSE | RESTRICTIONS -- *
 Subject to the terms and conditions of this license, the copyright holders
 hereby grant you a non-exclusive, non-transferable, revocable license to use,
 copy, modify, and distribute the software.
 You are expressly prohibited from selling or commercially exploiting the software.
 Unauthorized use, reproduction, or distribution of the software, or any part
 thereof, is strictly prohibited.

 * -- AUTHORIZED USERS | INTELLECTUAL PROPERTY -- *
 This license is granted only to the individual or entity to whom the software is
 directly distributed by the copyright holders. Any use by other individuals or
 entities is unauthorized and in violation of this license. The software is
 protected by copyright and other intellectual property laws.
 The copyright holders retain all rights, title, and interest in the software.

 * -- GOVERNING LAW -- *
 This license is governed by and construed in accordance with the laws of Romania.
 Any legal action or proceeding related to this license shall be brought exclusively
 in the courts of Romania.

 [Weddify x Diaphan Stationery] © [2024] All rights reserved.
 -------------------------------------------------------------------------------------- **/

import type {Action, ThunkAction} from "@reduxjs/toolkit"
import {combineSlices, configureStore} from "@reduxjs/toolkit"
import {setupListeners} from "@reduxjs/toolkit/query"
import {themesApi} from "../../services/themesApi";
import {plansApi} from "../../services/plansApi";
import {contactApi} from "../../services/contactApi";
import {orderApi} from "../../services/orderApi";
import {setupApi} from "../../services/setupApi";
import {tiersApi} from "../../services/tiersApi";
import {statisticsApi} from "../../services/statisticsApi";

const rootReducer = combineSlices({
    [themesApi.reducerPath]: themesApi.reducer,
    [plansApi.reducerPath]: plansApi.reducer,
    [contactApi.reducerPath]: contactApi.reducer,
    [orderApi.reducerPath]: orderApi.reducer,
    [setupApi.reducerPath]: setupApi.reducer,
    [tiersApi.reducerPath]: tiersApi.reducer,
    [statisticsApi.reducerPath]: statisticsApi.reducer,
});

export type RootState = ReturnType<typeof rootReducer>

export const makeStore = (preloadedState?: Partial<RootState>) => {
    const store = configureStore({
        reducer: rootReducer,
        middleware: getDefaultMiddleware => {
            return getDefaultMiddleware()
                .concat(themesApi.middleware)
                .concat(plansApi.middleware)
                .concat(contactApi.middleware)
                .concat(orderApi.middleware)
                .concat(setupApi.middleware)
                .concat(tiersApi.middleware)
                .concat(statisticsApi.middleware);
        },
        preloadedState,
    })
    setupListeners(store.dispatch)
    return store
}

export const store = makeStore()
export type AppStore = typeof store
export type AppDispatch = AppStore["dispatch"]
export type AppThunk<ThunkReturnType = void> = ThunkAction<
    ThunkReturnType,
    RootState,
    unknown,
    Action
>